














import {
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  computed,
  useContext,
  watch
} from '@nuxtjs/composition-api';
import {
  autocomplete,
  getAlgoliaResults,
  AutocompleteSource,
  
} from '@algolia/autocomplete-js';
import type { AutocompletePlugin } from '@algolia/autocomplete-js';
import liteClient from 'algoliasearch/lite';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';

import '@algolia/autocomplete-theme-classic/dist/theme.css';
import { useVSFContext } from '@vue-storefront/core';
import { useCurrentUser, useTranslations } from '@vue-storefront/novulo';
import useWindowResize from '~/hooks/useResizeWindow';
import { useSortingOptions } from '~/composables/useSortingOptions';

/* @ts-ignore */
import brandsAndCategories from '@/assets/brands-categories.json';
/* @ts-ignore */
import fallbackImage from '@/assets/images/Kamera_Express_faded_logo.webp';
import { addCurrency, capitalize, priceFormat } from '~/helpers/utils';

type ProductItem = {
  productID: string;
  name: string;
  productURL?: string;
  price: number;
  primaryImage: string;
  priceAfterCashback: any;
  priceAfterDiscount: number;
  actionPrice: number;
};

type FacetItem = {
  label: string;
  count: number;
};

type BrandItem = {
  name: string;
  image: string;
  url: string;
};

type CategoryItem = {
  name: string;
  link: string;
};

interface Brand {
  name: string;
  url: string;
}

interface Category {
  name: string;
  url: string;
}

interface LanguageData {
  wt: number[];
  brands: Brand[];
  categories: Category[];
}

interface BrandsAndCategories {
  [key: string]: LanguageData;
}

function getLanguageData(wt: number) {
  const data = brandsAndCategories.brandsAndCategories as BrandsAndCategories;
  return Object.values(data).find((lang) => lang.wt.includes(wt));
}

export default defineComponent({
  setup(_, { emit }) {
    const { getUsernameCookie } = useCurrentUser();
    const autocompleteRoot = ref<HTMLElement | null>(null);
    let autocompleteInstance: ReturnType<typeof autocomplete> | null = null;
    const hasInitialized = ref(false);
    const latestRedirectUrl = ref<string | null>(null);

    const { translate } = useTranslations();

    const { isDesktop } = useWindowResize();

    const { $novulo, i18n } = useVSFContext();
    const { $config } = useContext();

    const searchQuery = ref('');
    const initialSearchQuery = ref('');
    const isOverlayActive = ref(false);
    const redirectUrl = ref<string | null>(null);

    const wt = $novulo.config.state.getWt();
    const vsfContext = useVSFContext();

    priceFormat(0);

    const websiteGroup = computed(() => {
      return (
        vsfContext.$novulo.config.state?.domainConfig?.websiteGroup?.toLowerCase() ||
        ''
      );
    });

    const { indexName } = useSortingOptions();
    const appId = $config.algoliaAppId;
    const searchKey = $config.algoliaSearchKey;

    const rawClient = liteClient(appId, searchKey);
    const MIN_LENGTH = 3;

    let initialResults: any[] | null = null;

    const searchClient = {
      ...rawClient,
      search(requests: any[]) {
        const getQuery = (r: any) =>
          (r.params && r.params.query) ?? r.query ?? '';

        const allShort = requests.every((r) => {
          const q = getQuery(r);
          return q.length > 0 && q.length < MIN_LENGTH;
        });

        // reuse the initial results instead of reusing last hits
        if (allShort && initialResults) {
          return Promise.resolve({ results: initialResults });
        }

        // otherwise hit Algolia normally
        return rawClient.search(requests).then((response: any) => {
          const allEmpty = requests.every((r) => getQuery(r).length === 0);

          if (allEmpty && !initialResults) {
            initialResults = response.results;
          }

          if (allShort && !initialResults) {
            initialResults = response.results;
          }

          return response;
        });
      }
    } as any;

    const placeholder = computed(() => {
      const message = capitalize(translate('flyout_search_products'));
      const name = getUsernameCookie();
      if (!name) {
        return message;
      }

      return `${i18n.t('searchbar_hi_message')}${capitalize(name)}, ${message.toLowerCase()}`;
    });

    const langPathPrefix = computed(() => {
      const domainConfig = $novulo.config.state.getDomainConfig();
      const languages = Object.keys(domainConfig.lang);
      return languages[0] === i18n.locale ? '' : `/${i18n.locale}`;
    });

    const brandItems = computed(() => {
      const langData = getLanguageData(wt);
      if (!langData) return [];

      return langData.brands.map((brand) => ({
        name: brand.name,
        image: brandsAndCategories.logos[brand.name] || '',
        url: `${langPathPrefix.value}${brand.url}`
      }));
    });

    const categories = computed(() => {
      const langData = getLanguageData(wt);
      if (!langData) return [];

      return langData.categories.map((category) => ({
        name: category.name,
        link: `${langPathPrefix.value}${category.url}`
      }));
    });

    function hideOverlay() {
      isOverlayActive.value = false;
      autocompleteInstance?.setIsOpen(false);
      const inputElement = autocompleteRoot.value?.querySelector(
        '.aa-Input'
      ) as HTMLInputElement;
      if (inputElement) {
        inputElement.blur();
      }
      document.body.classList.remove('no-scroll');
    }

    watch(isOverlayActive, (newValue) => {
      if (newValue) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }

      emit('overlayToggle', newValue);
    });

    const normalizeUrl = (url: string) => {
      if (!url) return url;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return url.startsWith('/') ? url : `/${url}`;
    };

    const getImgSrc = (src) => {
      if (!src || src === '/media//') {
        return fallbackImage;
      }
      const cleanSrc = src.replace(/^\/+/, '');

      return `https://www.kamera-express.nl/${cleanSrc}`;
    };

    const moreProductsLink = computed(() => {
      const base = `${langPathPrefix.value}${i18n.t('/search-results')}`;
      const q = encodeURIComponent(searchQuery.value.trim());
      return `${base}?q=${q}`;
    });

    const getQueryFromUrl = () => {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('q');
        return query || '';
      }
      return '';
    };

    const handleMoreProductsClick = (event: Event) => {
      event.preventDefault();

      window.location.href = latestRedirectUrl.value || redirectUrl.value || moreProductsLink.value;
    };

    onMounted(async () => {
      initialSearchQuery.value = getQueryFromUrl();
      searchQuery.value = initialSearchQuery.value;

      if (hasInitialized.value || !autocompleteRoot.value) return;
      hasInitialized.value = true;

      const querySuggestionsPlugin = createQuerySuggestionsPlugin<any>({
        searchClient,
        indexName: indexName + '_query_suggestions',
        getSearchParams() {
          return {
            hitsPerPage: 3,
            ruleContexts: []
          };
        },
        transformSource({ source }) {
          return {
            ...source,
            sourceId: 'suggestions',
            templates: {
              ...source.templates,
              header({ html, state }) {
                const title =
                  state.query.length >= 3
                    ? translate('flyout_suggestions_matching_query')
                    : translate('flyout_suggestions');
                return html`<h5 class="flyout__title flyout__title--suggestion">
                  ${title}
                </h5>`;
              },
              item({ item, html }) {
                const searchUrl = `${langPathPrefix.value}${i18n.t('/search-results')}?q=${encodeURIComponent(item.query)}`;

                return html`
                  <a href="${searchUrl}" class="aa-suggestion-link">
                    <div class="aa-ItemWrapper">
                      <div class="aa-ItemContent">
                        <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path
                              d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"
                            ></path>
                          </svg>
                        </div>
                        <div class="aa-ItemContentBody">
                          <div class="aa-ItemContentTitle">${item.query}</div>
                        </div>
                      </div>
                    </div>
                  </a>
                `;
              },
              noResults({ html }) {
                return html`<div class="aa-ItemWrapper no-results">
                  <div class="aa-ItemContent">
                    <div class="aa-ItemContentBody">
                      <div class="aa-ItemContentTitle">
                        ${translate('no_results_found')}
                      </div>
                    </div>
                  </div>
                </div>`;
              },
              footer({ html }) {
                return html`
                  <div class="flyout__help">
                    <h5 class="flyout__title">${translate('flyout_staff')}</h5>
                    <ul class="flyout__help-list">
                      <li>
                        <a
                          href="/${translate(websiteGroup.value === 'ps' ? 'flyout_find_store_link_ps' : 'flyout_find_store_link')}"
                          class="flyout__help-item"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M6.25 15.8333V8.74999H10M6.25 15.8333L2.5 15.787C2.5 15.787 2.5 12.4857 2.5 10.3704C2.5 8.43582 2.5 4.16666 2.5 4.16666H17.5V15.8333H13.75M6.25 15.8333H10M13.75 15.8333V8.74999H10M13.75 15.8333H10M10 15.8333V8.74999"
                              stroke="#646363"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          ${translate(websiteGroup.value === 'ps' ? 'flyout_find_store_ps' : 'flyout_find_store')}
                        </a>
                      </li>
                      <li>
                        <a
                          href="/${translate('flyout_customer_service_link')}"
                          class="flyout__help-item"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M10 9.16667V13.75M10 6.25V6.66667M17.5 10C17.5 10.9849 17.306 11.9602 16.9291 12.8701C16.5522 13.7801 15.9997 14.6069 15.3033 15.3033C14.6069 15.9997 13.7801 16.5522 12.8701 16.9291C11.9602 17.306 10.9849 17.5 10 17.5C9.01509 17.5 8.03982 17.306 7.12987 16.9291C6.21993 16.5522 5.39314 15.9997 4.6967 15.3033C4.00026 14.6069 3.44781 13.7801 3.0709 12.8701C2.69399 11.9602 2.5 10.9849 2.5 10C2.5 8.01088 3.29018 6.10322 4.6967 4.6967C6.10322 3.29018 8.01088 2.5 10 2.5C11.9891 2.5 13.8968 3.29018 15.3033 4.6967C16.7098 6.10322 17.5 8.01088 17.5 10Z"
                              stroke="#646363"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          ${translate('flyout_customer_service')}
                        </a>
                      </li>
                    </ul>
                  </div>
                `;
              }
            }
          } as typeof source;
        }
      }) as AutocompletePlugin<any, any>;

      const inputElement = autocompleteRoot.value.querySelector(
        '.aa-Input'
      ) as HTMLInputElement;
      let wasOpen = true;

      autocompleteInstance = autocomplete<
        ProductItem | FacetItem | BrandItem | CategoryItem
      >({
        container: autocompleteRoot.value,
        placeholder: placeholder.value,
        initialState: {
          query: initialSearchQuery.value
        },
        openOnFocus: true,
        plugins: [querySuggestionsPlugin],
        onStateChange({ state }) {
          const query = state.query.trim();

          if (searchQuery.value !== query) {
            searchQuery.value = query;
          }

          redirectUrl.value = null;
          const redirectFromPlugin = (state as any)?.context?.redirectUrlPlugin?.data?.[0]?.urls?.[0];
          if (redirectFromPlugin) {
            redirectUrl.value = redirectFromPlugin;
          }

          isOverlayActive.value = state.isOpen;

          if (query.length === 0 && wasOpen) {
            wasOpen = false;
            autocompleteInstance?.setIsOpen(false);
          }

          if (query.length > 0) {
            wasOpen = true;
          }
        },
        getSources({
          query
        }): Array<
          AutocompleteSource<ProductItem | FacetItem | BrandItem | CategoryItem>
        > {
          return [
            {
              sourceId: 'products',
              getItems() {
                return getAlgoliaResults<ProductItem>({
                  searchClient,
                  queries: [
                    {
                      indexName,
                      params: {
                        query,
                        hitsPerPage: 3,
                        ruleContexts: [],
                        filters: 'segment:true',
                        enableRules: true
                      }
                    }
                  ],
                  transformResponse: ({hits, results}: any): any => {
                    /* eslint-disable no-empty */
                    try {
                      const first = Array.isArray(results) ? (results[0] as any) : null;
                      const fromUserData =
                        Array.isArray(first?.userData)
                          ? first.userData
                            .map((u: any) => u?.redirect?.url || u?.redirect)
                            .find(Boolean)
                          : null;

                      latestRedirectUrl.value = first?.renderingContent?.redirect?.url ?? fromUserData ?? null;
                    } catch (e) {

                    }

                    return hits as any;
                  }
                });
              },
              templates: {
                header({ html, state }) {
                  const title =
                    state.query.length >= 3
                      ? translate('flyout_products_matching_query')
                      : translate('flyout_relevant_products');
                  return html`<h5 class="flyout__title">${title}</h5>`;
                },
                item({ item, html }) {
                  const p = item as ProductItem;

                  const price = addCurrency(priceFormat(p.priceAfterDiscount));
                  const cashbackPrice = addCurrency(priceFormat(p.priceAfterCashback));

                  const cashbackPriceHtml = p.priceAfterCashback && p.priceAfterCashback !== 0
                    ? html`<span class="flyout-product__promo">
                        ${translate('flyout_price_after_cashback')} ${cashbackPrice}
                        </span>`
                    : '';

                  return html`
                    <article class="flyout-product">
                      <img
                        class="flyout-product__img"
                        src="${getImgSrc(p.primaryImage)}"
                        alt="${p.name}"
                      />
                      <div class="flyout-product__info">
                        <span class="flyout-product__name h8">${p.name}</span>
                        <span class="flyout-product__price">${price}</span>
                        ${cashbackPriceHtml}
                      </div>
                    </article>
                  `;
                },
                noResults({ html }) {
                  return html`<div class="aa-ItemWrapper no-results">
                    <div class="aa-ItemContent">
                      <div class="aa-ItemContentBody">
                        <div class="aa-ItemContentTitle">
                          ${translate('no_results_found')}
                        </div>
                      </div>
                    </div>
                  </div>`;
                },
                footer({ html }) {
                  return html`<a
                    href="#"
                    class="flyout__more"
                    onclick="${handleMoreProductsClick}"
                  >
                    <button class="sf-button--text no-underline sf-button">
                      <svg
                        data-v-255dc129=""
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        data-v-75a5fd6c=""
                      >
                        <path
                          d="M20 12L14 6M4 12H20H4ZM20 12L14 18L20 12Z"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </svg>
                      ${translate('flyout_more_products')}
                    </button></a
                  >`;
                }
              },
              onSelect({ item }) {
                const product = item as ProductItem;

                const prefix = langPathPrefix.value || '';

                const productUrl = product.productURL
                  ? normalizeUrl(product.productURL)
                  : '';

                const url = productUrl
                  ? (productUrl.startsWith('http://') || productUrl.startsWith('https://'))
                    ? productUrl
                    : `${prefix}${productUrl}`
                  : `/${translate('flyout_product')}/${product.productID}`;

                window.location.href = url;
              }
            },
            {
              sourceId: 'brands',
              getItems() {
                return brandItems.value;
              },
              templates: {
                header({ html }) {
                  return html`<h5 class="flyout__title">
                    ${translate('flyout_featured_brands')}
                  </h5>`;
                },
                item({ item, html }) {
                  const b = item as BrandItem;
                  return html`<span class="flyout-brand"
                    ><a href=${b.url}><img src=${b.image} /></a
                  ></span>`;
                },
                footer({ html }) {
                  return html`<a
                    href="/${translate('flyout_more_brands_link')}"
                    class="flyout__more"
                    ><button class="sf-button--text no-underline sf-button">
                      <svg
                        data-v-255dc129=""
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        data-v-75a5fd6c=""
                      >
                        <path
                          d="M20 12L14 6M4 12H20H4ZM20 12L14 18L20 12Z"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </svg>
                      ${translate('flyout_more_brands')}
                    </button></a
                  >`;
                }
              }
            },
            {
              sourceId: 'categories',
              getItems() {
                return categories.value;
              },
              templates: {
                header({ html }) {
                  return html`<h5 class="flyout__title">
                    ${translate('flyout_featured_categories')}
                  </h5>`;
                },
                item({ item, html }) {
                  const b = item as CategoryItem;
                  return html`<span class="flyout-category"
                    ><a href="${b.link}">${b.name}</a></span
                  >`;
                }
              }
            }
          ];
        },
        detachedMediaQuery: 'none'
      });

      if (initialSearchQuery.value && inputElement) {
        inputElement.value = initialSearchQuery.value;
      }

      const searchInput = autocompleteRoot.value.querySelector(
        '.aa-Input'
      ) as HTMLInputElement;
      if (searchInput) {
        searchInput.setAttribute('enterkeyhint', 'search');
      }

      if (isDesktop.value && inputElement) {
        inputElement.addEventListener('focus', () => {
          autocompleteInstance?.setIsOpen(true);
          isOverlayActive.value = true;
        });

        inputElement.addEventListener('click', () => {
          autocompleteInstance?.setIsOpen(true);
          isOverlayActive.value = true;
        });
      }

      inputElement?.addEventListener('blur', () => {
        setTimeout(() => {
          if (searchQuery.value.trim().length === 0) {
            isOverlayActive.value = false;
          }
        }, 150);
      });

      autocompleteRoot.value.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && searchQuery.value.trim().length > 0) {
          event.preventDefault();
          isOverlayActive.value = false;
          const url = latestRedirectUrl.value;
          window.location.href = url || moreProductsLink.value;
        }
      });

      autocompleteRoot.value.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;

        if (target.classList.contains('aa-ClearIcon')) {
          hideOverlay();
          autocompleteInstance?.setIsOpen(false);

          const inputElement = autocompleteRoot.value?.querySelector(
            '.aa-Input'
          ) as HTMLInputElement;

          if (inputElement) {
            inputElement.value = '';
            inputElement.blur();
            searchQuery.value = '';
          }
        }
      });

      const flyoutRoot = autocompleteRoot.value.closest(
        '.algolia-autocomplete-wrapper'
      );
      if (flyoutRoot) {
        emit('initialized');
      }
    });

    onUnmounted(() => {
      if (autocompleteInstance) {
        autocompleteInstance.destroy();
        autocompleteInstance = null;
      }
    });

    const shouldCenterFlyout = computed(() =>
      ['fg', 'pg', 'ps'].includes(websiteGroup.value)
    );

    watch(shouldCenterFlyout, (val) => {
      if (typeof document === 'undefined') {
        return;
      }

      if (val) {
        document.body.classList.add('center-flyout');
      } else {
        document.body.classList.remove('center-flyout');
      }
    }, { immediate: process.client });

    return {
      autocompleteRoot,
      moreProductsLink,
      placeholder,
      searchQuery,
      shouldCenterFlyout,
      isOverlayActive,
      getImgSrc,
      hideOverlay
    };
  }
});
